import {useTranslation} from '@aboutyou/i18n';
import {lazy} from '@aboutyou/lazy-imports';
import {
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Icon,
  mediaDesktopAndUp,
  mediaLargeScreens,
} from '@aboutyou/ui-components';
import {css, cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React from 'react';
import {
  HEADER_DEFAULT_PADDING,
  SEARCH_BAR_TABLET_WIDTH,
  SEARCH_BAR_WIDTH,
} from '../constants';
import {SearchBarResults} from './SearchBarResults';
import {useSearchBar} from './hooks/useSearchBar';

export interface SearchResultsFlyoutProp {
  searchValue: string;
  isInputFocused: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  shouldBubbleOnClick?: boolean;
  onRemoveInputFocus?: (searchTerm: string) => void;
  BasTeaserComponent?: React.ReactNode;
}

interface SearchBarProps {
  isSearchBarExpanded: boolean;
  expandSearchBar: () => void;
  collapseSearchBar: () => void;
  searchResultsFlyout: (props: SearchResultsFlyoutProp) => React.ReactNode;
}

const BASSearchTeaser = lazy(() =>
  import('../../../features/BASSearchTeaser/BASSearchTeaser').then(mod => ({
    default: mod.BASSearchTeaser,
  })),
);

export const SearchBar: React.FC<SearchBarProps> = ({
  isSearchBarExpanded,
  expandSearchBar,
  collapseSearchBar,
  searchResultsFlyout,
}) => {
  const {t} = useTranslation();
  const {
    searchBarRef,
    wrapperRef,
    inputRef,
    isTabletMode,
    isInputFocused,
    shouldShowSearchResults,
    shouldShowBasSearchTeaser,
    shouldShowCloseIcon,
    promotionData,
    searchValue,
    onFocusSearchInput,
    onRemoveInputFocusAndAddSearchTermToLocalStorage,
    handleInputOnChange,
    handleRemoveInputFocus,
    handleOnClickSearchIcon,
    handleCloseIconTap,
  } = useSearchBar({isSearchBarExpanded, collapseSearchBar, expandSearchBar});

  return (
    <Wrapper
      ref={wrapperRef}
      data-testid="searchBar"
      isSearchBarExpanded={isSearchBarExpanded}
    >
      <SearchBarWrapper
        className={cx(
          isSearchBarExpanded
            ? SearchWrapperExpandedCss
            : SearchWrapperClosedCss,
          isTabletMode ? SearchBarWrapperTablet : SearchBarWrapperDesktop,
        )}
        ref={searchBarRef}
      >
        <StyledSearchIcon
          asset={GenericIconName.Search}
          size={isTabletMode && !isSearchBarExpanded ? 'S' : 'XS'}
          data-testid="searchIcon"
          onClick={handleOnClickSearchIcon}
        />
        <StyledInput
          data-testid="searchBarInput"
          placeholder={t('Search for items, brands and more...')}
          ref={inputRef}
          onChange={e => handleInputOnChange(e.target.value)}
          onFocus={onFocusSearchInput}
        />
        {shouldShowCloseIcon && (
          <StyledCloseIcon
            asset={GenericIconName.Close}
            size="S"
            fill={COLORS.Grey1}
            data-testid="searchBarCloseIcon"
            onClick={handleCloseIconTap}
          />
        )}
      </SearchBarWrapper>
      {shouldShowSearchResults && (
        <SearchBarResults
          searchBarRef={searchBarRef}
          isSearchBarExpanded={isSearchBarExpanded}
          data-testid="searchBarCloseIcon"
          onClick={handleRemoveInputFocus}
        >
          {searchResultsFlyout({
            searchValue,
            isInputFocused,
            inputRef,
            shouldBubbleOnClick: false,
            BasTeaserComponent: shouldShowBasSearchTeaser && promotionData && (
              <BASSearchTeaser
                promotionTracking={{
                  creativeName: '',
                  creativeSlot: '',
                  promotionId: promotionData.promotionTracking.id,
                  promotionName: promotionData.promotionTracking.name,
                }}
                image={promotionData.image}
                target={promotionData.target}
              />
            ),
            onRemoveInputFocus:
              onRemoveInputFocusAndAddSearchTermToLocalStorage,
          })}
        </SearchBarResults>
      )}
    </Wrapper>
  );
};

export const Wrapper = styled.div<{isSearchBarExpanded: boolean}>`
  width: 100%;
  max-width: ${props =>
    props.isSearchBarExpanded ? 'unset' : `${SEARCH_BAR_WIDTH}px`};
`;

export const StyledSearchIcon = styled(Icon)`
  cursor: pointer;
  margin: 0 8px;
`;

export const StyledInput = styled.input`
  width: 100%;
  background: ${COLORS.Grey4};
  font-size: ${FONT_SIZES.px12};

  @media ${mediaDesktopAndUp} {
    padding-top: 2px;
    width: calc(100% - 36px);
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

export const StyledCloseIcon = styled(Icon)`
  margin-right: 4px;
  cursor: pointer;
`;

export const SearchBarWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 9px;
  right: ${HEADER_DEFAULT_PADDING.tablet}px;
  border-radius: 4px;
`;

const SearchWrapperExpandedCss = css`
  background: ${COLORS.Grey4};
  border-top: 8px solid white;
  border-bottom: 8px solid white;
  top: 0;
  height: 100%;
  width: calc(100% - ${HEADER_DEFAULT_PADDING.tablet * 2}px);

  @media ${mediaDesktopAndUp} {
    width: calc(100% - ${HEADER_DEFAULT_PADDING.desktop * 2}px);
    right: 30px;
  }

  @media ${mediaLargeScreens} {
    width: calc(100% - ${HEADER_DEFAULT_PADDING.largeScreens * 2}px);
  }
`;

const SearchWrapperClosedCss = css`
  width: ${SEARCH_BAR_TABLET_WIDTH}px;
  overflow: hidden;
  border: none;
  height: 36px;

  ${StyledSearchIcon} {
    position: relative;
    left: 50%;
    transform: translate(-${HEADER_DEFAULT_PADDING.tablet / 2}px);
  }

  ${StyledInput} {
    display: none;
  }

  ${StyledCloseIcon} {
    display: none;
  }
`;

export const SearchBarWrapperTablet = css`
  @media ${mediaDesktopAndUp} {
    right: 28px;
  }
  @media ${mediaLargeScreens} {
    right: ${HEADER_DEFAULT_PADDING.largeScreens}px;
  }
`;

const SearchBarWrapperDesktop = css`
  position: relative;
  top: unset;
  right: unset;
  width: 100%;
  border-color: ${COLORS.Grey4};
  background: ${COLORS.Grey4};

  ${StyledSearchIcon} {
    position: relative;
    left: unset;
    transform: unset;
  }

  ${StyledInput} {
    display: block;
  }

  ${StyledCloseIcon} {
    display: none;
  }
`;
